import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlaidLink } from 'react-plaid-link';
import api from '../utils/api';
import { OnboardingContext } from '../contexts/OnboardingContext';
import OnboardGuard from '../components/OnboardGuard';
import Logo from '../components/common/Logo';
import Footer from '../components/common/Footer';
import OnboardingProgressBar from '../components/common/OnboardingProgressBar';
import { Building2, CheckCircle, Plus, ArrowRight } from 'lucide-react';

function ConnectBankInner() {
  const navigate = useNavigate();
  const { setOnboardingStepIfNotComplete } = useContext(OnboardingContext);

  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [error, setError] = useState('');
  const [linkToken, setLinkToken] = useState(null);

  async function loadAccounts() {
    try {
      setLoadingAccounts(true);
      const { data } = await api.get('/api/plaid/accounts-by-institution');
      setAccounts(data.accounts || []);
    } catch (e) {
      console.error('Failed to fetch accounts:', e);
      setError('Failed to load accounts. Please refresh.');
    } finally {
      setLoadingAccounts(false);
    }
  }

  async function refreshLinkToken() {
    try {
      const { data } = await api.post('/api/plaid/create-link-token', {});
      setLinkToken(data.link_token);
    } catch (e) {
      console.error('Failed to create link token:', e);
      setError('Could not start Plaid Link. Try again.');
    }
  }

  useEffect(() => {
    loadAccounts();
    refreshLinkToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: async (public_token, metadata) => {
      try {
        await api.post('/api/plaid/exchange', { public_token, metadata });
        await loadAccounts();
        // if you want first-time auto-advance, you could check accounts.length === 0 here.
        // We’ll leave advancement to the “Next” button per your decision.
      } catch (err) {
        console.error('Exchange error:', err);
        setError('Bank linking failed. Please try again.');
      } finally {
        // get a fresh link token for the next “Link another bank”
        refreshLinkToken();
      }
    },
    onExit: (err) => {
      if (err) {
        console.error('Plaid Link exit error:', err);
        setError('Plaid Link was closed or errored. Try again.');
      }
    },
  });

  const handleNext = async () => {
    try {
      await api.post('/api/user/onboarding', { step: 'review_detected' });
      setOnboardingStepIfNotComplete('review_detected');
      navigate('/review-detected');
    } catch (e) {
      console.error('Failed to advance onboarding:', e);
      setError('Could not advance onboarding. Please try again.');
    }
  };

  const handleSkip = async () => {
    try {
      await api.post('/api/user/onboarding', { step: 'complete' });
      setOnboardingStepIfNotComplete('complete');
      navigate('/dashboard');
    } catch (err) {
      console.error('Failed to skip bank connection:', err);
      setError('Could not skip. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col">
      {/* Header with logo */}
      <div className="w-full py-6 px-4 border-b border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto flex justify-center">
          <Logo size="sm" />
        </div>
      </div>

      {/* Progress bar */}
      <OnboardingProgressBar currentStep="connect_bank" />

      {/* Main content - centered single column */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-3xl">
          <div className="mb-6 sm:mb-8 lg:mb-10">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-blue-100 p-3 rounded-full">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 text-center">
              Connect Your Bank
            </h1>
            <p className="text-gray-600 text-center text-base sm:text-lg">
              Connect your bank to auto-detect recurring bills and power your 8-week cash flow forecast
            </p>
          </div>

          {error && (
            <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              <div className="font-semibold">⚠️ Error</div>
              <p className="mt-1">{error}</p>
            </div>
          )}

          {/* Linked Accounts Section */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Linked Accounts</h3>
            
            {loadingAccounts ? (
              <div className="flex items-center justify-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Loading accounts…</span>
              </div>
            ) : accounts.length > 0 ? (
              <ul className="space-y-3">
                {accounts.map(a => (
                  <li key={a.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900">
                        {a.institution_name || 'Bank Account'}
                      </div>
                      <div className="text-sm text-gray-600 mt-0.5">
                        {a.name || 'Account'}
                        {a.subtype && ` • ${a.subtype}`}
                        {a.mask && ` • ****${a.mask}`}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p>No accounts linked yet</p>
                <p className="text-sm mt-1">Click below to connect your first bank account</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => (ready && linkToken ? open() : null)}
              disabled={!ready || !linkToken}
              className="flex items-center justify-center gap-2 min-h-[44px] px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <Plus className="w-5 h-5" />
              {accounts.length ? 'Link Another Bank' : 'Connect Bank with Plaid'}
            </button>

            <button
              onClick={handleNext}
              disabled={accounts.length === 0}
              title={accounts.length === 0 ? 'Link at least one bank first' : 'Continue'}
              className="flex items-center justify-center gap-2 min-h-[44px] px-6 py-3 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              Scan for Bills
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>

          {/* Warning when no accounts linked */}
          {accounts.length === 0 && (
            <p className="text-center text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 mt-3">
              Link at least one bank account to scan for recurring bills.
            </p>
          )}

          {/* Skip link */}
          <div className="text-center mt-4">
            <button
              onClick={handleSkip}
              className="text-sm text-gray-500 hover:text-gray-700 underline transition-colors"
            >
              Skip for now — I'll connect later
            </button>
          </div>

          <p className="text-center text-sm text-gray-500 mt-6">
            Your bank credentials are encrypted and secure. We use Plaid to safely connect your accounts.
          </p>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default function ConnectBank() {
  return (
    <OnboardGuard currentStep="connect_bank">
      <ConnectBankInner />
    </OnboardGuard>
  );
}